import {Component,createRef,type ReactNode} from 'react';
import type {AwardStreamBoundary} from '@platform/client';
import {registerCardRankMotion} from '@platform/ui/cardMotion';

type Props={drawId:string;eventId?:string;sequence:number;stage:string;active:boolean;stream:AwardStreamBoundary;pageKey:string;ids:string[];children:ReactNode};
type Position={x:number;y:number;top:number;bottom:number};
type Before=Map<string,Position>|null;

// FLIP: read the old visible page immediately BEFORE React moves its keyed
// cards, then animate only changed positions. No polling, frame loop, cloned
// cards or layout reads on ordinary pool updates. Off-page promotions enter
// from just below their new position instead of flying through the whole list.
export class RankedCardGrid extends Component<Props,{},Before>{
 private grid=createRef<HTMLDivElement>();
 private motions=new Map<HTMLElement,{animation:Animation;release:()=>void}>();
 private reduced:MediaQueryList|null=null;
 private stop=()=>{for(const [node,motion] of this.motions){motion.release();motion.animation.cancel();delete node.dataset.rankMotion;}this.motions.clear();};
 private onPreference=()=>{if(this.reduced?.matches)this.stop();};
 private onVisibility=()=>{if(document.hidden)this.stop();};
 componentDidMount(){this.reduced=matchMedia('(prefers-reduced-motion: reduce)');this.reduced.addEventListener('change',this.onPreference);document.addEventListener('visibilitychange',this.onVisibility);}
 componentWillUnmount(){this.stop();this.reduced?.removeEventListener('change',this.onPreference);document.removeEventListener('visibilitychange',this.onVisibility);}
 getSnapshotBeforeUpdate(prev:Props):Before{
  const next=this.props,grid=this.grid.current;
  const ready=(p:Props)=>p.stream.ready&&p.stream.currentDrawId===p.drawId;
  const fresh=next.sequence===prev.sequence+1&&next.eventId!==prev.eventId;
  const stageChanged=next.sequence===prev.sequence&&next.eventId===prev.eventId&&next.stage!==prev.stage;
  if(!grid||!next.active||!ready(prev)||!ready(next)||document.hidden||this.reduced?.matches||
   next.drawId!==prev.drawId||next.stream.generation!==prev.stream.generation||next.pageKey!==prev.pageKey||
   !(fresh||stageChanged)||next.ids.join('|')===prev.ids.join('|'))return null;
  const container=grid.getBoundingClientRect();
  const positions=new Map<string,Position>();
  for(const node of grid.querySelectorAll<HTMLElement>(':scope > .bingo-card')){
   const r=node.getBoundingClientRect(),id=node.dataset.cardId!;
   positions.set(id,{x:r.left-container.left,y:r.top-container.top,top:r.top,bottom:r.bottom});
  }
  this.stop();return positions;
 }
 componentDidUpdate(prev:Props,_state:{},before:Before){
  const p=this.props;
  if(!p.active||p.drawId!==prev.drawId||p.pageKey!==prev.pageKey||!p.stream.ready||p.stream.currentDrawId!==p.drawId||p.stream.generation!==prev.stream.generation){this.stop();return;}
  const grid=this.grid.current;if(!before||!grid){if(p.ids.join('|')!==prev.ids.join('|'))this.stop();return;}
  const container=grid.getBoundingClientRect(),order=new Map(p.ids.map((id,i)=>[id,i])),oldOrder=new Map(prev.ids.map((id,i)=>[id,i]));
  const style=getComputedStyle(grid),raw=style.getPropertyValue('--motion-card-rank').trim();
  const duration=parseFloat(raw)*(raw.endsWith('ms')?1:1000);
  if(!Number.isFinite(duration)||duration<=0)return;
  const lift=parseFloat(style.getPropertyValue('--card-rank-lift')),scale=style.getPropertyValue('--card-rank-scale').trim();
  // Batch all geometry/style reads before starting any animations.
  const moves=[...grid.querySelectorAll<HTMLElement>(':scope > .bingo-card')].map(node=>{
   const r=node.getBoundingClientRect(),id=node.dataset.cardId!,old=before.get(id),index=order.get(id)!;
   const rising=(oldOrder.get(id)??index)>index;
   const wasVisible=!!old&&old.bottom>0&&old.top<innerHeight,visible=r.bottom>0&&r.top<innerHeight;
   const fullPath=wasVisible&&visible;
   return {node,rising,visible:visible||wasVisible,old,dx:fullPath?old.x-(r.left-container.left):0,dy:fullPath?old.y-(r.top-container.top):rising?lift:0,shadow:getComputedStyle(node).boxShadow};
  });
  for(const m of moves){
   if(!m.visible||!m.rising&&(!m.old||Math.abs(m.dx)+Math.abs(m.dy)<1))continue;
   const rest=m.shadow,raised=style.getPropertyValue('--card-rank-shadow').trim()+(rest==='none'?'':','+rest);
   const frames:Keyframe[]=m.rising?[
    {transform:`translate(${m.dx}px,${m.dy}px) scale(1)`,boxShadow:rest,offset:0},
    {transform:`translate(${m.dx*.55}px,${m.dy*.55}px) scale(${scale})`,boxShadow:raised,offset:.35},
    {transform:'translate(0,0) scale(1)',boxShadow:rest,offset:1}
   ]:[{transform:`translate(${m.dx}px,${m.dy}px)`},{transform:'translate(0,0)'}];
   m.node.dataset.rankMotion=m.rising?'rise':'shift';
   const animation=m.node.animate(frames,{duration,easing:'cubic-bezier(.22,.7,.25,1)'});
   animation.startTime=document.timeline.currentTime as number??performance.now();
   const release=registerCardRankMotion(m.node,p.eventId??'',animation);
   this.motions.set(m.node,{animation,release});
   animation.onfinish=()=>{release();animation.cancel();if(this.motions.get(m.node)?.animation===animation){this.motions.delete(m.node);delete m.node.dataset.rankMotion;}};
  }
 }
 render(){return <div ref={this.grid} className="card-grid owned-card-grid">{this.props.children}</div>;}
}
